import React, { InputHTMLAttributes } from 'react';
import BodyText from '../Typography/BodyText/BodyText';
import Div from '../Element/Div/Div';
import Flex from '../Flex/Flex';
import Gutter from '../Gutter/Gutter';
import SmallText from '../Typography/SmallText/SmallText';
import styles from './Checkbox.module.scss';
import { CreateProps } from '../../types/utils/CreateProps';
import SpinningLoader from '../SpinningLoader/SpinningLoader';
import CheckboxIcon from './CheckboxIcon/CheckboxIcon';

type CheckboxProps = CreateProps<
  {
    /** State of the checkbox. Mixed should be used only in the group of checkboxes to indicate that some of the boxes in the group are selected */
    checked?: boolean | 'mixed';
    /** Label of the checkbox */
    label?: React.ReactNode;
    /** Optional description text. This is also for showing possible errors */
    description?: React.ReactNode;
    /** On checkbox state change event handler */
    onChange?: (
      event:
        | React.ChangeEvent<HTMLInputElement>
        | React.KeyboardEvent<HTMLDivElement>,
      checked: boolean
    ) => void;
    /** Disable checkbox */
    disabled?: boolean;
    /** Checkbox has an error, use it with description prop to give a descriptive message for th error */
    error?: boolean;
    /** When true, displays loader instead of the checkbox */
    loading?: boolean;
    /** Name of the form control. Submitted with the form as a name of value, if the checkbox is toggled on */
    name?: InputHTMLAttributes<HTMLInputElement>['name'];
    /** The value of the checkbox when submitting the form, if the checkbox is toggled on */
    value?: InputHTMLAttributes<HTMLInputElement>['value'];
  },
  typeof Div
>;

const Checkbox: React.FunctionComponent<CheckboxProps> = ({
  loading = false,
  checked,
  label,
  description,
  onChange,
  disabled,
  error,
  className,
  name,
  value,
  ...props
}) => {
  const blur = (event: any) => {
    event.preventDefault();
  };
  return (
    <Div
      className={[
        styles.checkbox,
        className,
        {
          [styles.checkbox__checked]: !!checked,
          [styles.checkbox__disabled]: disabled,
          [styles.checkbox__loading]: loading,
        },
      ]}
      {...props}
    >
      <label className={styles.checkbox_label}>
        <Flex>
          {loading ? (
            <SpinningLoader className={styles.checkbox_loader} size="sm" />
          ) : (
            <>
              <input
                onMouseDown={blur}
                className={styles.checkbox_hiddenCheckbox}
                type="checkbox"
                checked={!!checked}
                tabIndex={-1}
                onChange={(event) => !disabled && onChange?.(event, !checked)}
                name={name}
                value={value}
              />

              <Div
                borderRadius={{ size: 'sm' }}
                backgroundColor="white"
                className={[
                  styles.checkbox_fakeCheckbox,
                  'checkbox-test',
                  {
                    [styles.checkbox_fakeCheckbox_error]: error,
                    [styles.checkbox_fakeCheckbox__checked]: !!checked,
                    [styles.checkbox_fakeCheckbox__disabled]: disabled,
                  },
                ]}
                onMouseDown={blur}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(event) => {
                  event.keyCode === 13 &&
                    !disabled &&
                    onChange?.(event, !checked);
                }}
              >
                <CheckboxIcon
                  color={disabled ? 'gray400' : 'white'}
                  iconName={checked === 'mixed' ? 'minus' : 'checkmark'}
                  className={styles.checkbox_checkIcon}
                />
              </Div>
            </>
          )}

          {(label || description) && (
            <>
              <Gutter size="xs" />

              <div>
                {label ? (
                  <BodyText
                    tagName="span"
                    color={disabled ? 'gray400' : 'black'}
                    block
                  >
                    {label}
                  </BodyText>
                ) : (
                  <Gutter size="md" />
                )}

                {description && (
                  <>
                    <Gutter size="xxs" />
                    <SmallText
                      className={styles.checkbox_description}
                      color={error ? 'red600' : 'gray500'}
                      block
                    >
                      {description}
                    </SmallText>
                  </>
                )}
              </div>
            </>
          )}
        </Flex>
      </label>
    </Div>
  );
};

export default Checkbox;
